import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  inject,
  InjectionToken,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { STATUS } from '../../common';
import { ApiCallHandler } from '../../common/api-call-handler';
import { EoFormComponent, EoFormPanelConfig } from '../../form';
import { PaginatedSearchResult } from '../../partial/eo-search-panel/paginated-search-result.model';
import { SearchPageOptionsService } from '../../partial/eo-search-panel/search-page-options.service';
import { TableConfig } from '../../table/model';
import { EoSearchPageConfig } from './eo-search-page-config.model';

export interface IDataService<T> {
  searchCh: ApiCallHandler<PaginatedSearchResult<T> | null>;
  exportExcelCh?: ApiCallHandler<any>;
  init(): void;
}

interface messageParams {
  message: string;
  header?: string;
  accept?: () => void;
}

export const DATA_SERVICE_TOKEN = new InjectionToken<IDataService<any>>(
  'DataService'
);

@Component({
  selector: 'eo-search-page',
  templateUrl: './eo-search-page.component.html',
  styleUrl: './eo-search-page.component.scss',
})
export class EoSearchPageComponent<T> {
  public confirmationService = inject(ConfirmationService);
  public searchOptions = inject(SearchPageOptionsService);
  private changeDetector = inject(ChangeDetectorRef);
  @ViewChild('eoForm') eoForm?: EoFormComponent;

  viewOptions$ = this.searchOptions?.getViewOptions();
  defaultSearchParam: any;
  searchParam: any;

  submitBtnLabel = 'Αναζήτηση';

  protected formReady = true;

  sortOptions?: { sortField: string; sortOrder: 'ASC' | 'DESC' };

  private _config?: EoSearchPageConfig<T>;
  get config(): EoSearchPageConfig<T> | undefined {
    return this._config;
  }
  @Input() set config(val: EoSearchPageConfig<T>) {
    this._config = val;
  }

  @Input() formConfig: EoFormPanelConfig[] = [];
  @Input() collapsed = true;
  @Input() tableConfig?: TableConfig<T>;
  @Input() loading = false;
  @Input() viewModeVisible = true;
  @Input() actionMenuVisible = true;
  @Input() customFormControls: any;
  @Input() menuActions: MenuItem[] = [];
  @Input() searchServ?: IDataService<T>;
  @Input() totalCount?: number;
  @Input() page?: number;
  @Input() pagesize?: number;
  @Input() paginator = true;
  @Input() initData = true;
  @Input() instructionsVisible = true;
  @Input() instructionsMenuItem?: MenuItem;
  @Input() showClearButton = true;
  @Input() sort: boolean = false;

  private _exportExcelVisible?: boolean;
  @Input() set exportExcelVisible(val: boolean | undefined) {
    this._exportExcelVisible = val;
  }
  get exportExcelVisible(): boolean {
    if (this._exportExcelVisible != undefined) {
      return this._exportExcelVisible;
    }
    if (typeof this.config?.exportExcelVisible === 'function') {
      return this.config?.exportExcelVisible();
    }
    return this.config?.exportExcelVisible ?? false;
  }

  private _createNewVisible?: boolean;
  @Input() set createNewVisible(val: boolean | undefined) {
    this._createNewVisible = val;
  }
  get createNewVisible(): boolean {
    if (this._createNewVisible != undefined) {
      return this._createNewVisible;
    }
    if (typeof this.config?.createNewVisible === 'function') {
      return this.config?.createNewVisible();
    }
    return this.config?.createNewVisible ?? false;
  }

  private _createNewDisabled?: boolean;
  @Input() set createNewDisabled(val: boolean | undefined) {
    this._createNewDisabled = val;
  }
  get createNewDisabled(): boolean {
    if (this._createNewDisabled != undefined) {
      return this._createNewDisabled;
    }

    if (typeof this.config?.createNewDisabled === 'function') {
      return this.config?.createNewDisabled();
    }

    return this.config?.createNewDisabled ?? false;
  }

  private _exportExcelDisabled?: boolean;
  @Input() set exportExcelDisabled(val: boolean | undefined) {
    this._exportExcelDisabled = val;
  }
  get exportExcelDisabled(): boolean {
    if (this.isLoading() || this.loading) return true;

    if (this._exportExcelDisabled != undefined) {
      return this._exportExcelDisabled;
    }

    if (typeof this.config?.exportExcelDisabled === 'function') {
      return this.config?.exportExcelDisabled();
    }

    return this.config?.exportExcelDisabled ?? false;
  }

  @Output() search = new EventEmitter<any>();
  @Output() clearEvent = new EventEmitter<void>();
  @Output() formChange = new EventEmitter<any>();
  @Output() createNew = new EventEmitter<void>();
  @Output() exportExcel = new EventEmitter<void>();

  get formGroup(): FormGroup {
    return this.eoForm?.getForm() ?? new FormGroup({});
  }

  _sortMode?: 'single' | 'multiple';
  @Input() set sortMode(val: 'single' | 'multiple') {
    this._sortMode = val;
  }
  get sortMode(): 'single' | 'multiple' {
    return this._sortMode ?? this._config?.sortMode ?? 'single';
  }

  get customSort(): boolean {
    return this.config?.sort ?? false;
  }

  constructor(
    @Inject(DATA_SERVICE_TOKEN) protected searchService: IDataService<T>
  ) {
    if (this.searchServ) searchService = this.searchServ;
  }

  ngAfterContentInit(): void {
    this.changeDetector.detectChanges();
  }

  ngOnInit(): void {
    this.searchOptions
      .captureEvent('window:resize')
      .subscribe(() =>
        this.searchOptions.onIsMobileChange(window.innerWidth < 768)
      );
    this.eoForm?.form?.reset();
    if (this.initData) this.onSubmit(this.defaultSearchParam);
  }

  onViewModeChange($event: 'GRID' | 'TABLE') {
    this.searchOptions?.setViewMode($event);
  }

  onPageChange(event: any) {
    this.searchOptions?.setCurrentPage(event.page + 1);
    this.searchOptions?.setPageSize(event.rows);
    this.search.emit(this.searchParam);
  }

  onSubmit(searchParams?: any) {
    if (!searchParams && this.eoForm)
      searchParams = EoFormPanelConfig.getFormData(this.eoForm.getForm());

    if (this.sortOptions != undefined) {
      searchParams.sortOptions = this.sortOptions;
    }

    this.searchParam = searchParams;
    this.searchOptions?.setCurrentPage(1);
    this.search.emit(this.searchParam);
  }

  onViewModeSelect(mode: 'GRID' | 'TABLE') {
    this.searchOptions?.setViewMode(mode);
  }

  onTest() {
    console.log(this.eoForm?.form);
  }

  onClear() {
    this.searchService?.init();
    this.eoForm?.form?.reset();
    this.sortOptions = undefined;

    this.refreshForm();

    let searchParams = {};

    if (this.eoForm)
      searchParams = EoFormPanelConfig.getFormData(this.eoForm.getForm());

    if (this.initData) this.onSubmit(searchParams);
    this.clearEvent.emit();
  }

  onSort(event: any): void {
    if (
      event.field === this.sortOptions?.sortField &&
      ((this.sortOptions?.sortOrder === 'ASC' && event.order > 0) ||
        (this.sortOptions?.sortOrder === 'DESC' && event.order < 0))
    )
      return;

    if (!event) {
      this.sortOptions = undefined;
    } else {
      this.sortOptions = {
        sortField: event.field,
        sortOrder: event.order > 0 ? 'ASC' : 'DESC',
      };
    }

    this.onSubmit();
  }

  onCreateNewClick() {
    this.createNew.emit();
  }

  onExportExcelClick() {
    this.exportExcel.emit();
  }

  refreshForm(force: boolean = false) {
    if (!force && !this.formHasDropdowns()) return;

    this.formReady = false;
    setTimeout(() => {
      this.formReady = true;
    }, 1);
  }

  formHasDropdowns(): boolean {
    if (!this.eoForm || !this.eoForm.form) return false;

    let formHasDropdowns = false;
    const controls = this.eoForm.config as EoFormPanelConfig[];
    controls.forEach((control) => {
      control.config.forEach((c) => {
        if (
          c.type === 'dropdown' &&
          c.control.hasValidator(Validators.required)
        ) {
          formHasDropdowns = true;
        }
      });
    });
    return formHasDropdowns;
  }

  markAllControlsAsTouchedAndUpdateValidity(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup) {
        this.markAllControlsAsTouchedAndUpdateValidity(control);
      } else {
        control.markAsUntouched();
        control.markAsPristine();
        control.updateValueAndValidity();
      }
    });

    formGroup.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  }

  onFormChange($event: any): void {
    this.formChange.emit($event);
  }

  isMessageParams(message: any): message is messageParams {
    return (message as messageParams) !== undefined;
  }

  showSuccessMessage(message: messageParams | string, header: string = ' ') {
    if (this.isMessageParams(message)) {
      let header = message.header;
      if (!header) {
        header = ' ';
      }

      this.confirmationService?.confirm({
        header: header,
        icon: 'pi pi-check-circle',
        message: message.message ?? 'Η διαδικασία ολοκληρώθηκε με επιτυχία.',
        rejectLabel: 'Εντάξει',
        rejectIcon: '-',
        rejectButtonStyleClass: 'p-button-text',
        acceptVisible: false,
        accept: message.accept,

        closeOnEscape: true,
        key: 'searchPageMessage',
      });
    } else {
      this.confirmationService?.confirm({
        header: header,
        icon: 'pi pi-check-circle',
        message: message,
        rejectLabel: 'Εντάξει',
        rejectIcon: '-',
        rejectButtonStyleClass: 'p-button-text',
        acceptVisible: false,
        closeOnEscape: true,
        key: 'searchPageMessage',
      });
    }
  }

  isGridView(): boolean {
    return this.viewOptions$?.getValue().viewMode === 'GRID';
  }

  isTableView(): boolean {
    return this.viewOptions$?.getValue().viewMode === 'TABLE';
  }

  isLoading(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.loading;
  }

  isSearchPending(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.success;
  }

  isSearchSuccess(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.success;
  }

  getResults(): T[] | null {
    const searchResults = this.searchServ?.searchCh?.data$.getValue();
    if (searchResults?.Resultset?.length != 0) {
      return searchResults?.Resultset ?? [];
    }

    if (searchResults?.resultset?.length != 0) {
      return searchResults?.resultset ?? [];
    }

    return [];
  }

  getTotalCount(): number {
    const searchResults = this.searchService?.searchCh?.data$.getValue();
    return (
      this.totalCount ??
      searchResults?.TotalCount ??
      searchResults?.totalCount ??
      0
    );
  }

  getPage(): number {
    return this.page ?? this.viewOptions$?.getValue().currentPage ?? 0;
  }

  getPagesize(): number {
    return this.pagesize ?? this.viewOptions$?.getValue().pageSize ?? 10;
  }

  getFormConfig() {
    return this.formConfig;
  }

  isSubmitLoading(): boolean {
    let status = this.searchService?.searchCh?.status$?.getValue();
    return status === STATUS.loading;
  }

  getTableConfig(): TableConfig<T> | undefined {
    return undefined;
  }

  isNoDataLabelVisible(): boolean {
    return this.isSearchSuccess() && this.getTotalCount() === 0;
  }

  isTableVisible(): boolean {
    if (!this.isTableView()) return false;
    if (!this.isLoading() && this.getTotalCount() === 0) return false;
    return true;
  }

  isPaginatorVisible(): boolean {
    if (!this.paginator) return false;
    return this.getTotalCount() > 0;
  }

  getShowClearButton(): boolean {
    return this.showClearButton;
  }
}
